import {
  VersionRowBase,
  VersionStatus,
  VersioningBase
} from '../shared/versioning/types'
import { FILL_LEVELS, FillLevel } from '../shared/dosing_modes/fillLevels'

// Decimals arrive from the API as strings ("2.0")
type ApiNumber = number | string | null

type NumericValue = number | null

interface ParameterValues {
  id?: number
  pressure: NumericValue
  duty_cycle: NumericValue
  frequency: NumericValue
}

export interface LevelValues {
  id?: number
  fill_level: FillLevel
  fast_parameter: ParameterValues
  slow_parameter: ParameterValues
}

interface EmergencySettingValues {
  id?: number
  max_attempts: NumericValue
  residual_delivery_amount: NumericValue
  max_dosing_time_ms: NumericValue
  low_fill_max_dosing_time_ms: NumericValue
  no_change_max_time_ms: NumericValue
  no_change_amount: NumericValue
}

export interface EmergencyStepValues extends ParameterValues {
  step_number: number
  duration_ms: NumericValue
  _destroy?: boolean
}

interface BlowerSettingValues {
  id?: number
  enabled: boolean
  air_pressure: NumericValue
  activation_delay_ms: NumericValue
  blowing_duration_on_ms: NumericValue
  blowing_duration_off_ms: NumericValue
}

export interface FeederDosingModeValues {
  id?: number
  name: string
  description: string
  high_fill_min_percent: NumericValue
  medium_fill_min_percent: NumericValue
  unknown_fill_fallback: FillLevel | ''
  levels: LevelValues[]
  emergency_setting: EmergencySettingValues
  emergency_steps: EmergencyStepValues[]
  go_back_enabled: boolean
  go_back_duration_ms: NumericValue
  go_back_parameter: ParameterValues
  pusher_enabled: boolean
  max_acceptable_shortfall_percent: NumericValue
  standard_blower_setting: BlowerSettingValues
  emergency_blower_setting: BlowerSettingValues
}

interface ParameterRecord {
  id: number
  pressure: ApiNumber
  duty_cycle: ApiNumber
  frequency: ApiNumber
}

type BlowerSettingRecord =
  | (Omit<BlowerSettingValues, 'id' | 'air_pressure'> & {
      id: number
      air_pressure: ApiNumber
    })
  | null

export interface FeederDosingModeRecord {
  id: number
  name: string
  description: string | null
  status: VersionStatus
  version_number: number
  high_fill_min_percent: number | null
  medium_fill_min_percent: number | null
  unknown_fill_fallback: FillLevel
  go_back_enabled: boolean
  go_back_duration_ms: number | null
  pusher_enabled: boolean
  max_acceptable_shortfall_percent: number
  levels: {
    id: number
    fill_level: FillLevel
    fast_parameter: ParameterRecord | null
    slow_parameter: ParameterRecord | null
  }[]
  go_back_parameter: ParameterRecord | null
  emergency_setting:
    (Omit<EmergencySettingValues, 'id'> & { id: number }) | null
  emergency_steps: (ParameterRecord & {
    step_number: number
    duration_ms: number
  })[]
  standard_blower_setting: BlowerSettingRecord
  emergency_blower_setting: BlowerSettingRecord
}

export interface CopySourceFeederDosingMode extends FeederDosingModeRecord {
  average_rating: number | null
  comments_count: number
}

export interface VersionRow extends VersionRowBase {
  averageRating: number | null
  commentsCount: number
}

export interface VersioningProps extends VersioningBase<VersionRow> {
  usedByProducts: string[]
  affectedLocations: string[]
}

export interface FeederDosingModeErrors {
  errors: string[]
  field_errors: Record<string, string[]>
}

const toNumber = (value: ApiNumber): NumericValue =>
  value === null || value === '' ? null : Number(value)

const emptyParameter = (): ParameterValues => ({
  pressure: null,
  duty_cycle: null,
  frequency: null
})

// Durations are edited in seconds and converted here; the fields keep the API's _ms names so server
// errors still map onto them
const MS_PER_SECOND = 1000

export const msToSeconds = (value: NumericValue): NumericValue =>
  value === null ? null : value / MS_PER_SECOND

const secondsToMs = (value: NumericValue): NumericValue =>
  value === null ? null : Math.round(value * MS_PER_SECOND)

const emergencySettingValues = (
  record: FeederDosingModeRecord['emergency_setting']
): EmergencySettingValues | null =>
  record && {
    ...record,
    max_dosing_time_ms: msToSeconds(record.max_dosing_time_ms),
    low_fill_max_dosing_time_ms: msToSeconds(
      record.low_fill_max_dosing_time_ms
    ),
    no_change_max_time_ms: msToSeconds(record.no_change_max_time_ms)
  }

const emergencySettingPayload = (setting: EmergencySettingValues) => ({
  ...setting,
  max_dosing_time_ms: secondsToMs(setting.max_dosing_time_ms),
  low_fill_max_dosing_time_ms: secondsToMs(setting.low_fill_max_dosing_time_ms),
  no_change_max_time_ms: secondsToMs(setting.no_change_max_time_ms)
})

const parameterValues = (record: ParameterRecord | null): ParameterValues =>
  record
    ? {
        id: record.id,
        pressure: toNumber(record.pressure),
        duty_cycle: toNumber(record.duty_cycle),
        frequency: toNumber(record.frequency)
      }
    : emptyParameter()

const emptyBlowerSetting = (): BlowerSettingValues => ({
  enabled: false,
  air_pressure: null,
  activation_delay_ms: null,
  blowing_duration_on_ms: null,
  blowing_duration_off_ms: null
})

const blowerSettingValues = (
  record: BlowerSettingRecord
): BlowerSettingValues =>
  record
    ? {
        ...record,
        air_pressure: toNumber(record.air_pressure),
        activation_delay_ms: msToSeconds(record.activation_delay_ms),
        blowing_duration_on_ms: msToSeconds(record.blowing_duration_on_ms),
        blowing_duration_off_ms: msToSeconds(record.blowing_duration_off_ms)
      }
    : emptyBlowerSetting()

export const emptyEmergencyStep = (
  stepNumber: number
): EmergencyStepValues => ({
  ...emptyParameter(),
  step_number: stepNumber,
  duration_ms: null
})

const emptyFeederDosingMode = (): FeederDosingModeValues => ({
  name: '',
  description: '',
  high_fill_min_percent: null,
  medium_fill_min_percent: null,
  unknown_fill_fallback: '',
  levels: FILL_LEVELS.map((fillLevel) => ({
    fill_level: fillLevel,
    fast_parameter: emptyParameter(),
    slow_parameter: emptyParameter()
  })),
  emergency_setting: {
    max_attempts: null,
    residual_delivery_amount: null,
    max_dosing_time_ms: null,
    low_fill_max_dosing_time_ms: null,
    no_change_max_time_ms: null,
    no_change_amount: null
  },
  emergency_steps: [emptyEmergencyStep(1)],
  go_back_enabled: false,
  go_back_duration_ms: null,
  go_back_parameter: emptyParameter(),
  pusher_enabled: false,
  max_acceptable_shortfall_percent: null,
  standard_blower_setting: emptyBlowerSetting(),
  emergency_blower_setting: emptyBlowerSetting()
})

export const feederDosingModeValues = (
  record: FeederDosingModeRecord | undefined
): FeederDosingModeValues => {
  const empty = emptyFeederDosingMode()
  if (!record) return empty

  return {
    id: record.id,
    name: record.name,
    description: record.description ?? '',
    high_fill_min_percent: record.high_fill_min_percent,
    medium_fill_min_percent: record.medium_fill_min_percent,
    unknown_fill_fallback: record.unknown_fill_fallback,
    levels: empty.levels.map((emptyLevel) => {
      const level = record.levels.find(
        (candidate) => candidate.fill_level === emptyLevel.fill_level
      )
      return level
        ? {
            id: level.id,
            fill_level: level.fill_level,
            fast_parameter: parameterValues(level.fast_parameter),
            slow_parameter: parameterValues(level.slow_parameter)
          }
        : emptyLevel
    }),
    emergency_setting:
      emergencySettingValues(record.emergency_setting) ??
      empty.emergency_setting,
    emergency_steps: record.emergency_steps.length
      ? record.emergency_steps.map((step) => ({
          ...parameterValues(step),
          step_number: step.step_number,
          duration_ms: msToSeconds(step.duration_ms)
        }))
      : empty.emergency_steps,
    go_back_enabled: record.go_back_enabled,
    go_back_duration_ms: msToSeconds(record.go_back_duration_ms),
    go_back_parameter: parameterValues(record.go_back_parameter),
    pusher_enabled: record.pusher_enabled,
    max_acceptable_shortfall_percent: record.max_acceptable_shortfall_percent,
    standard_blower_setting: blowerSettingValues(
      record.standard_blower_setting
    ),
    emergency_blower_setting: blowerSettingValues(
      record.emergency_blower_setting
    )
  }
}

// Every parameter of the source replaces the form's; the name, description and the ids of the current
// records stay so the update keeps editing the same mode and its existing children. Saved steps cannot
// be matched to the source's, so the current ones are removed and the copied ones added as new rows.
export const copyParameters = (
  current: FeederDosingModeValues,
  source: FeederDosingModeRecord
): FeederDosingModeValues => {
  const copied = feederDosingModeValues(source)
  const currentLevel = (fillLevel: FillLevel) =>
    current.levels.find((level) => level.fill_level === fillLevel)

  return {
    ...copied,
    id: current.id,
    name: current.name,
    description: current.description,
    levels: copied.levels.map((level) => {
      const existing = currentLevel(level.fill_level)
      return {
        ...level,
        id: existing?.id,
        fast_parameter: {
          ...level.fast_parameter,
          id: existing?.fast_parameter.id
        },
        slow_parameter: {
          ...level.slow_parameter,
          id: existing?.slow_parameter.id
        }
      }
    }),
    emergency_setting: {
      ...copied.emergency_setting,
      id: current.emergency_setting.id
    },
    go_back_parameter: {
      ...copied.go_back_parameter,
      id: current.go_back_parameter.id
    },
    standard_blower_setting: {
      ...copied.standard_blower_setting,
      id: current.standard_blower_setting.id
    },
    emergency_blower_setting: {
      ...copied.emergency_blower_setting,
      id: current.emergency_blower_setting.id
    },
    emergency_steps: [
      ...current.emergency_steps
        .filter((step) => step.id)
        .map((step) => ({ ...step, _destroy: true })),
      ...copied.emergency_steps.map(({ id: _copiedId, ...step }) => step)
    ]
  }
}

const blowerSettingPayload = (blower: BlowerSettingValues) =>
  blower.enabled
    ? {
        ...blower,
        activation_delay_ms: secondsToMs(blower.activation_delay_ms),
        blowing_duration_on_ms: secondsToMs(blower.blowing_duration_on_ms),
        blowing_duration_off_ms: secondsToMs(blower.blowing_duration_off_ms)
      }
    : { enabled: false }

// Removed saved steps stay in the array (marked _destroy) so error indexes from the server line up
// with the form; the remaining steps are renumbered so the robot slots stay contiguous.
const emergencyStepsPayload = (steps: EmergencyStepValues[]) => {
  let stepNumber = 0
  return steps.map((step) =>
    step._destroy
      ? { id: step.id, _destroy: true }
      : {
          ...step,
          duration_ms: secondsToMs(step.duration_ms),
          step_number: (stepNumber += 1)
        }
  )
}

// Switched-off go-back and blowers send only their flag, so the saved values are kept for later
export const feederDosingModePayload = (values: FeederDosingModeValues) => ({
  feeder_dosing_mode: {
    name: values.name,
    description: values.description,
    high_fill_min_percent: values.high_fill_min_percent,
    medium_fill_min_percent: values.medium_fill_min_percent,
    unknown_fill_fallback: values.unknown_fill_fallback,
    levels_attributes: values.levels.map((level) => ({
      id: level.id,
      fill_level: level.fill_level,
      fast_parameter_attributes: level.fast_parameter,
      slow_parameter_attributes: level.slow_parameter
    })),
    emergency_setting_attributes: emergencySettingPayload(
      values.emergency_setting
    ),
    emergency_steps_attributes: emergencyStepsPayload(values.emergency_steps),
    go_back_enabled: values.go_back_enabled,
    ...(values.go_back_enabled && {
      go_back_duration_ms: secondsToMs(values.go_back_duration_ms),
      go_back_parameter_attributes: values.go_back_parameter
    }),
    pusher_enabled: values.pusher_enabled,
    max_acceptable_shortfall_percent: values.max_acceptable_shortfall_percent,
    standard_blower_setting_attributes: blowerSettingPayload(
      values.standard_blower_setting
    ),
    emergency_blower_setting_attributes: blowerSettingPayload(
      values.emergency_blower_setting
    )
  }
})
